/**
 * Shared HTTP transport for the React client.
 *
 * The legacy ASP.NET handlers do not use a single response envelope, so this
 * module normalizes JSON/text/blob responses and converts server failures to
 * user-friendly HttpError instances. All requests keep the current DNN session
 * cookie by using same-origin credentials.
 */
import { readRuntimeUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';

/** Error type that preserves both HTTP status and raw server response for diagnostics. */
export class HttpError extends Error {
  constructor(public readonly status: number, public readonly responseText: string, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
  responseType?: 'json' | 'text' | 'blob';
}

function readServerMessage(text: string, status: number): string {
  const copy = publicCopy(readRuntimeUiLocale());
  const trimmed = text.trim();
  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed === 'string' && parsed.trim()) return parsed.trim();
      if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        for (const key of ['Error', 'error', 'Message', 'message', 'ExceptionMessage']) {
          if (typeof record[key] === 'string' && record[key]!.trim()) return record[key]!.trim();
        }
      }
    } catch {
      const plain = trimmed.replace(/^"|"$/g, '');
      if (plain && !/^<!doctype|^<html/i.test(plain)) return plain.slice(0, 500);
    }
  }
  if (status === 400) return copy.badRequest;
  if (status === 401) return copy.loginRequired;
  if (status === 403) return copy.forbidden;
  if (status === 404) return copy.notFound;
  if (status === 409) return copy.conflict;
  if (status >= 500) return copy.serverError;
  return copy.requestError(status);
}

/**
 * Executes a same-origin request and normalizes the handler's mixed response formats.
 * FormData is passed through unchanged; all other bodies are serialized as JSON.
 */
export async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const headers: HeadersInit = { Accept: options.responseType === 'blob' ? '*/*' : 'application/json, text/plain, */*' };
  const init: RequestInit = {
    method: options.method ?? (options.body === undefined ? 'GET' : 'POST'),
    credentials: 'same-origin',
    signal: options.signal,
    cache: 'no-store'
  };

  if (options.body !== undefined) {
    if (options.body instanceof FormData) {
      init.body = options.body;
    } else {
      headers['Content-Type'] = 'application/json; charset=utf-8';
      init.body = JSON.stringify(options.body);
    }
  }
  init.headers = headers;

  const response = await fetch(url, init);
  if (!response.ok) {
    const text = await response.text();
    throw new HttpError(response.status, text, readServerMessage(text, response.status));
  }

  if (options.responseType === 'blob') return await response.blob() as T;
  const text = await response.text();
  if (options.responseType === 'text') return text as T;
  if (!text.trim()) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
}

/** Builds a URL for the legacy ASHX action dispatcher without concatenating unescaped query values. */
export function actionUrl(handler: 'FormsHandler' | 'FiltersHandler' | 'ReportsHandler' | 'MySubmissionsHandler' | 'AccessControlHandler' | 'FeatureManagementHandler', action: string, params?: Record<string, unknown>): string {
  const query = new URLSearchParams({ Action: action });
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  return `/DesktopModules/FaraDynamicForms/ajax/${handler}.ashx?${query.toString()}`;
}
